import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  MoreHorizontalIcon,
  RefreshCwIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  UnplugIcon,
  Trash2Icon,
} from "lucide-react";
import { usePermissions } from "../../PermissionsProvider";
import {
  VastInfraService,
  VastInstance,
  VastAdapter,
} from "../../services/VastInfraService";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

const STATUS_VARIANT: Record<string, string> = {
  RUNNING:
    "bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/20",
  PROVISIONING:
    "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/20",
  PENDING:
    "bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border-yellow-500/20",
  STOPPED: "bg-muted text-muted-foreground border-border",
  ERROR: "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/20",
};

interface ConfirmState {
  open: boolean;
  title: string;
  description: string;
  destructive?: boolean;
  onConfirm: () => void;
}

function adapterLabel(a: VastAdapter): string {
  const runTag = a.finetune_run_id ? `Run #${a.finetune_run_id}` : "No run";
  return `${runTag}: ${a.hosted_name || a.model_name}`;
}

const VastInstancesOverview: React.FC = () => {
  const { apiKey } = usePermissions();
  const [instances, setInstances] = useState<VastInstance[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [confirm, setConfirm] = useState<ConfirmState>({
    open: false,
    title: "",
    description: "",
    onConfirm: () => {},
  });

  const fetchInstances = useCallback(() => {
    VastInfraService.getInstances(setIsLoading, apiKey).subscribe(
      (data: VastInstance[]) => setInstances(data),
      (error: any) => {
        console.error("Failed to load instances:", error);
        toast.error("Failed to load instances");
      }
    );
  }, [apiKey]);

  useEffect(() => {
    fetchInstances();
    const interval = setInterval(fetchInstances, 30000);
    return () => clearInterval(interval);
  }, [fetchInstances]);

  const toggleRow = (id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDestroyInstance = (record: VastInstance) => {
    VastInfraService.deleteInstance(record.id, false, null, apiKey).subscribe(
      (result: { status: string }) => {
        if (result.status === "not_found_on_vast") {
          setConfirm({
            open: true,
            title: "Instance Not Found",
            description: `Instance ${record.instance_id || record.id} no longer exists on Vast. Clean up the stale entry?`,
            destructive: true,
            onConfirm: () => {
              VastInfraService.deleteInstance(record.id, true, null, apiKey).subscribe(
                () => {
                  toast.success("Stale entry removed");
                  fetchInstances();
                },
                () => toast.error("Failed to clean up entry")
              );
            },
          });
        } else {
          toast.success(`Instance ${record.instance_id || record.id} destroyed`);
          fetchInstances();
        }
      },
      () => toast.error("Failed to destroy instance")
    );
  };

  const handleOffloadAdapter = (infraId: number, adapter: VastAdapter) => {
    setConfirm({
      open: true,
      title: "Offload Adapter",
      description: `Unload "${adapterLabel(adapter)}" from this instance and disable the model?`,
      destructive: true,
      onConfirm: () => {
        VastInfraService.removeAdapter(
          infraId,
          adapter.model_id,
          null,
          apiKey
        ).subscribe(
          () => {
            toast.success(`Offloaded ${adapter.model_name}`);
            fetchInstances();
          },
          () => toast.error("Failed to offload adapter")
        );
      },
    });
  };

  const runningCount = instances.filter((i) => i.status === "RUNNING").length;
  const provisioningCount = instances.filter(
    (i) => i.status === "PROVISIONING"
  ).length;
  const totalAdapters = instances.reduce((sum, i) => sum + i.adapter_count, 0);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-2xl font-gc-demi-bold text-foreground">
          Instances
        </h2>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchInstances}
          disabled={isLoading}
        >
          <RefreshCwIcon
            className={`w-4 h-4 mr-1.5 ${isLoading ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        Inference instances and loaded LoRA adapters. Refreshes every 30s.
      </p>

      <div className="flex gap-2 mb-4">
        <Badge variant="secondary">{instances.length} total</Badge>
        <Badge className="bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/20">
          {runningCount} running
        </Badge>
        {provisioningCount > 0 && (
          <Badge className="bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/20">
            {provisioningCount} provisioning
          </Badge>
        )}
        <Badge variant="secondary">{totalAdapters} adapters loaded</Badge>
      </div>

      {isLoading && instances.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <RefreshCwIcon className="w-5 h-5 animate-spin mr-2" />
          Loading...
        </div>
      ) : instances.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          No instances found.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead className="w-16">ID</TableHead>
              <TableHead className="w-20">Provider</TableHead>
              <TableHead className="w-28">Instance</TableHead>
              <TableHead>Base Model</TableHead>
              <TableHead className="w-40">GPU</TableHead>
              <TableHead className="w-32">Status</TableHead>
              <TableHead className="w-24">Cost/hr</TableHead>
              <TableHead className="w-28">Adapters</TableHead>
              <TableHead className="w-36">Created</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {instances.map((inst) => (
              <React.Fragment key={inst.id}>
                {/* Instance row */}
                <TableRow
                  className={
                    inst.adapter_count > 0
                      ? "cursor-pointer"
                      : ""
                  }
                  onClick={() =>
                    inst.adapter_count > 0 && toggleRow(inst.id)
                  }
                >
                  <TableCell className="px-2">
                    {inst.adapter_count > 0 &&
                      (expandedRows.has(inst.id) ? (
                        <ChevronDownIcon className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronRightIcon className="w-4 h-4 text-muted-foreground" />
                      ))}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {inst.id}
                  </TableCell>
                  <TableCell className="text-xs capitalize">
                    {inst.provider}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {inst.instance_id || "—"}
                  </TableCell>
                  <TableCell className="font-gc-med">
                    {inst.base_model}
                  </TableCell>
                  <TableCell className="text-sm">
                    {inst.gpu_type
                      ? `${inst.gpu_count}x ${inst.gpu_type}`
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${
                        STATUS_VARIANT[inst.status] || STATUS_VARIANT.STOPPED
                      }`}
                    >
                      {inst.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm font-mono text-muted-foreground">
                    {inst.cost_per_hour != null
                      ? `$${inst.cost_per_hour.toFixed(2)}/hr`
                      : "—"}
                  </TableCell>
                  <TableCell className="text-sm">
                    <span
                      className={
                        inst.adapter_count >= inst.max_adapter_count
                          ? "text-red-500"
                          : "text-muted-foreground"
                      }
                    >
                      {inst.adapter_count}/{inst.max_adapter_count}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {inst.date_created
                      ? new Date(inst.date_created).toLocaleString(undefined, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })
                      : "—"}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                        >
                          <MoreHorizontalIcon className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => handleDestroyInstance(inst)}
                        >
                          <Trash2Icon className="w-4 h-4 mr-2" />
                          Destroy Instance
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>

                {/* Expanded adapters section */}
                {expandedRows.has(inst.id) && inst.adapters.length > 0 && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={11} className="bg-muted/30 px-0 py-0">
                      <div className="px-10 py-3 space-y-2">
                        <p className="text-xs font-gc-med text-muted-foreground uppercase tracking-wide mb-2">
                          Loaded LoRA Adapters
                        </p>
                        {inst.adapters.map((adapter) => (
                          <div
                            key={adapter.model_id}
                            className="flex items-center justify-between rounded-md border border-border bg-background px-4 py-2.5"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <Badge
                                variant={
                                  adapter.status === "ENABLED"
                                    ? "default"
                                    : "secondary"
                                }
                                className="shrink-0"
                              >
                                {adapter.status}
                              </Badge>
                              <div className="min-w-0">
                                <p className="text-sm font-gc-med text-foreground truncate">
                                  {adapterLabel(adapter)}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Company {adapter.company_id}
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0 ml-4"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOffloadAdapter(inst.id, adapter);
                              }}
                            >
                              <UnplugIcon className="w-3.5 h-3.5 mr-1.5" />
                              Offload
                            </Button>
                          </div>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      )}

      <AlertDialog
        open={confirm.open}
        onOpenChange={(open) => setConfirm((c) => ({ ...c, open }))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={
                confirm.destructive
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : ""
              }
              onClick={() => {
                confirm.onConfirm();
                setConfirm((c) => ({ ...c, open: false }));
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VastInstancesOverview;
